import React from 'react';
import { ArrowRight, Shield, TrendingUp, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const Hero = () => {
  return (
    <section id="home" className="relative h-[80vh] md:h-[80vh] flex items-center justify-center overflow-hidden pt-16 md:pt-24 pb-0">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/videos/bg-chc_nwi3vn.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy-900/40"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/50 to-navy-800/30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-4 md:py-2 lg:py-3">
        <div className="max-w-4xl mx-auto">
          {/* Main Heading */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl font-bold font-serif text-white mb-4 sm:mb-5 md:mb-4 lg:mb-3 leading-tight drop-shadow-lg" data-aos="fade-up" data-aos-delay="100">
            Elevate Your
            <span className="text-gold-400 block mt-2 sm:mt-2 md:mt-1.5 relative inline-block">
              Financial Future
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-gold-500 via-gold-400 to-gold-500 animate-underline-draw shadow-[0_0_8px_rgba(251,191,36,0.6)]"></span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-lg lg:text-base xl:text-lg text-gray-200 mb-6 sm:mb-7 md:mb-5 lg:mb-4 max-w-3xl mx-auto leading-relaxed md:leading-snug px-2 sm:px-0 drop-shadow-md" data-aos="fade-up" data-aos-delay="200">
            COREVEST delivers expert investment solutions across{' '}
            <span className="text-gold-400 font-semibold">Blockchain-based Assets, Real Estate, Oil and Gas, and Stocks</span>. 
            Unlock your wealth potential through strategic portfolio management and data-driven market insights.
          </p>

          {/* CTA Button */}
          <div className="flex justify-center items-center mb-6 sm:mb-7 md:mb-5 lg:mb-4 px-4 sm:px-0" data-aos="fade-up" data-aos-delay="300">
            <a 
              href="https://dashboard.corevests.com/register"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center justify-center px-8 sm:px-10 md:px-8 py-3 sm:py-4 md:py-3 text-base sm:text-lg lg:text-base font-bold text-white bg-gold-600 hover:bg-gold-700 rounded-full shadow-2xl hover:shadow-gold-600/25 transition-all duration-500 hover:scale-105 hover:-translate-y-1"
            >
              {/* Button content */}
              <span className="mr-3">Create Account</span>
              <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-2 transition-transform duration-300" />
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-4 lg:gap-5 max-w-lg sm:max-w-2xl mx-auto mt-5 sm:mt-6 md:mt-4 lg:mt-3">
            <div className="text-center group bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-3 md:p-3 lg:p-2 border border-white/20 hover:bg-white/15 transition-all duration-300 shadow-lg" data-aos="fade-up" data-aos-delay="400">
              <div className="flex items-center justify-center mb-1 sm:mb-2 md:mb-1">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 lg:h-4 text-gold-400 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-sm sm:text-lg md:text-lg lg:text-base font-bold text-white mb-0.5 sm:mb-1 md:mb-0.5">$4.2B+</div>
              <div className="text-gray-300 text-[9px] sm:text-xs md:text-xs lg:text-[10px] leading-tight">Assets Under Management</div>
            </div>
            <div className="text-center group bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-3 md:p-3 lg:p-2 border border-white/20 hover:bg-white/15 transition-all duration-300 shadow-lg" data-aos="fade-up" data-aos-delay="500">
              <div className="flex items-center justify-center mb-1 sm:mb-2 md:mb-1">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 lg:h-4 text-gold-400 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-sm sm:text-lg md:text-lg lg:text-base font-bold text-white mb-0.5 sm:mb-1 md:mb-0.5">40K+</div>
              <div className="text-gray-300 text-[9px] sm:text-xs md:text-xs lg:text-[10px] leading-tight">Active Clients</div>
            </div>
            <div className="text-center group bg-white/10 backdrop-blur-md rounded-lg p-2 sm:p-3 md:p-3 lg:p-2 border border-white/20 hover:bg-white/15 transition-all duration-300 shadow-lg" data-aos="fade-up" data-aos-delay="600">
              <div className="flex items-center justify-center mb-1 sm:mb-2 md:mb-1">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 md:h-5 lg:h-4 text-gold-400 group-hover:scale-110 transition-transform" />
              </div>
              <div className="text-sm sm:text-lg md:text-lg lg:text-base font-bold text-white mb-0.5 sm:mb-1 md:mb-0.5">10+</div>
              <div className="text-gray-300 text-[9px] sm:text-xs md:text-xs lg:text-[10px] leading-tight">Years Experience</div>
            </div>
          </div>
        </div>
      </div>


    </section>
  );
};

export default Hero;